/**
 * The admin's list of gardeners, for "view as" (card E16).
 *
 * API: GET /admin/grow-oversight/gardeners?q= -> { success, gardeners: [{
 * userId, name, email, place, postalCode, ... }] }: Grow admins only (the
 * server's GROW_ADMIN_EMAILS), at most 25, newest first; q searches id, email
 * or name. Anyone else is answered 403. Only the view-as page asks for it.
 *
 * Who is an admin is the account's own word: GET /users/firebase/:uid sends
 * `isAdmin` in the account (the server's Grow admin list, the same check the
 * admin routes use; true for an admin while viewing as a gardener). The header
 * reads it through useIsGrowAdmin and asks the server nothing (W24 Job C).
 *
 * Who uses it: features/admin/ViewAsPage (getAdminUsers) and the header's menu (useIsGrowAdmin).
 *
 * @module adminUsers
 */

import { apiClient } from './apiClient';
import { useAuth } from '@/shared/hooks/useAuth';

/** One gardener in the admin's list. */
export interface AdminUser {
  /** The gardener's user id. */
  userId: number;
  /** Their name. */
  name: string;
  /** Their email. */
  email: string;
  /** Where they garden (their place), as the server words it. */
  place: string | null;
}

/**
 * Lists the gardeners an admin can view as.
 *
 * @param q - Search text (id, email or name); empty lists the newest.
 * @returns The gardeners.
 */
export async function getAdminUsers(q = ''): Promise<AdminUser[]> {
  const { data } = await apiClient.get<{ gardeners: AdminUser[] }>('/admin/grow-oversight/gardeners', { params: q ? { q } : {} });
  return data.gardeners;
}

/**
 * Whether this person is a Grow admin, as the account answer says (`isAdmin`). No request: an account that says
 * false or says nothing is not an admin.
 *
 * @returns True only when the server said so.
 */
export function useIsGrowAdmin(): boolean {
  const { mysqlUser } = useAuth();
  return mysqlUser?.isAdmin === true;
}
